// src/api/meetings.ts
// API-клиент для встреч с кандидатами
import api from './client';

export interface MeetingAttendee {
  email: string;
  name?: string;
  role?: 'candidate' | 'hr' | 'manager' | 'guest';
}

export interface MeetingReminder {
  minutes: number;
  sent?: boolean;
  channel?: 'email' | 'push' | 'both';
}

export interface Meeting {
  _id: string;
  id?: string;
  candidateId: string;
  creatorId: string;
  companyId: string;
  title: string;
  description?: string;
  startsAt: string;
  endsAt: string;
  timezone?: string;
  allDay?: boolean;
  location?: string;
  meetingLink?: string;
  attendees?: MeetingAttendee[];
  status: 'scheduled' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
  cancelReason?: string;
  reminders?: MeetingReminder[];
  notes?: string;
  icsUid: string;
  comments?: MeetingComment[];
  yandexEventId?: string;
  createdAt: string;
  updatedAt: string;
  candidate?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  creator?: {
    _id: string;
    name?: string;
    email?: string;
  };
}

export interface MeetingComment {
  _id?: string;
  text: string;
  author: string;
  authorId?: string;
  createdAt: string;
}

export interface MeetingInput {
  candidateId: string;
  title: string;
  description?: string;
  startsAt: string;
  endsAt: string;
  timezone?: string;
  location?: string;
  meetingLink?: string;
  attendees?: MeetingAttendee[];
  reminders?: MeetingReminder[];
  notes?: string;
}

export const meetingsApi = {
  list: (params?: { from?: string; to?: string; candidateId?: string; status?: string }) =>
    api.get<Meeting[]>('/meetings', { params }).then(r => r.data),

  get: (id: string) =>
    api.get<Meeting>(`/meetings/${id}`).then(r => r.data),

  create: (data: MeetingInput) =>
    api.post<Meeting>('/meetings', data).then(r => r.data),

  update: (id: string, data: Partial<MeetingInput & { status: string; cancelReason?: string }>) =>
    api.put<Meeting>(`/meetings/${id}`, data).then(r => r.data),

  cancel: (id: string, reason?: string) =>
    api.delete(`/meetings/${id}`, { data: { reason } }).then(r => r.data),

  /** Добавить комментарий к встрече */
  addComment: (meetingId: string, text: string) =>
    api.post<{ ok: boolean; comment: MeetingComment; comments: MeetingComment[] }>(
      `/meetings/${meetingId}/comments`,
      { text }
    ).then(r => r.data),

  /** Удалить комментарий */
  removeComment: (meetingId: string, commentId: string) =>
    api.delete<{ ok: boolean; comments: MeetingComment[] }>(
      `/meetings/${meetingId}/comments/${commentId}`
    ).then(r => r.data),

  /** URL для скачивания .ics (прямая ссылка с авторизацией через query) */
  icsUrl: (id: string) => {
    const base = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
    return `${base}/meetings/${id}/ics`;
  },

  /** Скачать .ics с авторизацией — создаёт blob и триггерит download */
  downloadIcs: async (id: string, filename?: string) => {
    const response = await api.get(`/meetings/${id}/ics`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `meeting-${id}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};
